/*
 * Figma `122:411` — TODAY-04 · Credits & Billing.
 *
 * `ManagementTemplate`: the balance and what is blocking it, the ledger, and the lots beside them.
 *
 * The frame is drawn against a card-billing SaaS — a monthly spend cap, a forecast, auto top-up
 * above a threshold, a card on file ending 4242, a receipt to download. This product is none of
 * those things. Credits are prepaid lots bought by bank transfer: you pick packages, the server
 * quotes a price, you get an account number, you upload the slip, and somebody confirms it. There
 * is no card, no cap, no auto top-up and no receipt endpoint. A guardrail panel over a payment
 * method that does not exist would be a control that does nothing.
 *
 * What the frame leaves out is the part that costs real money, and both halves are kept:
 *
 * - **The forfeit.** Trading a still-live lot in for a discount closes the credits left in it. The
 *   confirmation states both sides — credits given up, baht received.
 * - **The collision.** A lot that expires before the event ends is money that will never be spent.
 *   That is a tile here, not a line in a list, because it is the one thing on this screen with a
 *   deadline attached.
 */
import { useState } from 'react';
import {
  Alert, Button, Card, ConfirmDialog, DataTable, Drawer, KpiCard, KpiGrid, ManagementTemplate,
  StatusBadge,
} from '../..';
import type { BadgeTone, Column } from '../..';
import type { Destination } from '../../destinations';
import { fmt } from '../../../api';
import type { CreditLedgerEntryDto, CreditLotDto, CreditTopupDto } from '../../../api';
import type { CreditsState } from './useCredits';
import { eligibleForTradeIn, pendingTopups, tradeInCost, useCredits } from './useCredits';

const KIND_LABEL: Record<string, string> = {
  purchase: 'ซื้อเครดิต', grant: 'ได้รับจากทีมงาน', free_tier: 'เครดิตฟรีของอีเวนต์',
  charge: 'ตัดค่าบัตรที่ขายได้', uncovered: 'ค้างจ่าย (เครดิตไม่พอ)', debt_settle: 'ชำระเครดิตค้างจ่าย',
  expire: 'หมดอายุ', carryover_out: 'แลกเป็นส่วนลด', carryover_in: 'ส่วนลดที่ได้รับ',
  refund: 'คืนเครดิต', admin_adjust: 'ปรับยอดโดยทีมงาน',
};

const SOURCE_LABEL: Record<CreditLotDto['source'], string> = {
  purchase: 'ซื้อ', grant: 'ทีมงานให้', free_tier: 'ฟรี',
};

const TOPUP_LABEL: Record<CreditTopupDto['status'], string> = {
  pending: 'รอแนบสลิป', awaiting_verification: 'รอตรวจสอบ', confirmed: 'เติมเครดิตแล้ว',
  rejected: 'ถูกปฏิเสธ', expired: 'หมดอายุใบเสนอราคา',
};
const TOPUP_TONE: Record<CreditTopupDto['status'], BadgeTone> = {
  pending: 'warning', awaiting_verification: 'accent', confirmed: 'success',
  rejected: 'danger', expired: 'neutral',
};

const credits = (n: number) => `${n.toLocaleString('th-TH')} ใบ`;
const dt = (iso: string) => new Date(iso).toLocaleString('th-TH');
const day = (iso: string) => new Date(iso).toLocaleDateString('th-TH');

function expiryWords(lot: CreditLotDto): string {
  if (lot.daysUntilExpiry === null) return 'ไม่มีวันหมดอายุ';
  if (lot.daysUntilExpiry < 0) return `หมดอายุแล้ว ${Math.abs(lot.daysUntilExpiry)} วัน`;
  if (lot.daysUntilExpiry === 0) return 'หมดอายุวันนี้';
  return `เหลืออีก ${lot.daysUntilExpiry} วัน`;
}

export function CreditsScreen({ destination, state, permitted, role }: {
  destination: Destination;
  state: CreditsState;
  permitted: boolean;
  role?: string | null;
}) {
  const {
    balance, tiers, topups, statement, statementLoaded, risks, loading, loadError, error, busy,
    picks, tradeIn, quote, quoteError,
    setPick, toggleTradeIn, chooseOnly, resetPurchase, buy, uploadSlip, loadStatement, reload,
  } = state;

  const [buying, setBuying] = useState(false);
  const [confirmBuy, setConfirmBuy] = useState(false);

  const eligible = eligibleForTradeIn(balance);
  const cost = tradeInCost(eligible, tradeIn);
  const waiting = pendingTopups(topups);
  const colliding = risks.filter((r) => r.diesBeforeEvent);
  const expiringCredits = risks.reduce((sum, r) => sum + r.lot.creditsRemaining, 0);

  const ledgerColumns: Column<CreditLedgerEntryDto>[] = [
    {
      key: 'kind',
      header: 'รายการ',
      cell: (r) => (
        <span className="jt-cell-stack">
          <span>{KIND_LABEL[r.kind] ?? r.kind}</span>
          {r.note && <span className="jt-cell-sub">{r.note}</span>}
        </span>
      ),
    },
    { key: 'at', header: 'เมื่อ', width: '180px', cell: (r) => dt(r.created_at) },
    {
      key: 'delta',
      header: 'เครดิต',
      align: 'end',
      width: '120px',
      cell: (r) => (r.delta === 0
        ? <span className="jt-cell-sub">—</span>
        : <span className="jt-mono">{r.delta > 0 ? '+' : ''}{r.delta.toLocaleString('th-TH')}</span>),
    },
    {
      key: 'thb',
      header: 'เงิน',
      align: 'end',
      width: '120px',
      cell: (r) => (r.amount_thb > 0 ? <span className="jt-mono">{fmt(r.amount_thb)}</span> : <span className="jt-cell-sub">—</span>),
    },
  ];

  return (
    <ManagementTemplate
      gate={{ destination, permitted, role, loading, error: loadError || null, onRetry: reload }}
      summary={
        <KpiGrid>
          <KpiCard
            label="เครดิตคงเหลือ"
            value={balance ? credits(balance.spendable) : '—'}
            meta="1 เครดิต = ผู้เข้างาน 1 คน · ตัดเมื่อออเดอร์ชำระเงินสำเร็จ"
          />
          <KpiCard
            label="ค้างจ่าย"
            value={balance ? credits(balance.debtCredits) : '—'}
            tone={balance && balance.debtCredits > 0 ? 'danger' : 'neutral'}
            meta={balance && balance.debtCredits > 0
              ? 'ระบบไม่ได้หยุดการขาย แต่จะหักจากยอดเติมครั้งถัดไปก่อน'
              : 'ไม่มีเครดิตค้างจ่าย'}
          />
          <KpiCard
            label="ใกล้หมดอายุ"
            value={credits(expiringCredits)}
            tone={colliding.length > 0 ? 'danger' : expiringCredits > 0 ? 'warning' : 'neutral'}
            meta={colliding.length > 0
              ? `${colliding.length} ก้อนหมดอายุก่อนงานจบ — เครดิตส่วนนี้จะไม่ได้ใช้`
              : expiringCredits > 0 ? 'ยังหมดอายุหลังงานจบ' : 'ไม่มีก้อนไหนใกล้หมดอายุ'}
          />
          <KpiCard
            label="รายการเติมที่ค้างอยู่"
            value={waiting.length.toLocaleString('th-TH')}
            tone={waiting.length > 0 ? 'warning' : 'neutral'}
            meta={waiting.length > 0
              ? 'เครดิตจะยังไม่เข้าจนกว่าจะแนบสลิปและมีคนตรวจ'
              : 'ไม่มีรายการที่รออยู่'}
          />
        </KpiGrid>
      }
      toolbar={
        <>
          <Button variant="primary" onClick={() => setBuying(true)}>ซื้อเครดิต</Button>
          <Button variant="ghost" onClick={() => void loadStatement()}>
            {statementLoaded ? 'รายการเดินบัญชีโหลดแล้ว' : 'ดูรายการเดินบัญชี'}
          </Button>
        </>
      }
      items={
        <>
          {error && <Alert tone="danger" title="เกิดข้อผิดพลาด">{error}</Alert>}

          {balance && !balance.billingEnabled && (
            <Alert tone="warning" title="อีเวนต์นี้ยังไม่ได้เปิดใช้ระบบเครดิต">
              บัตรที่ขายได้จะยังไม่ถูกตัดเครดิต — ยอดคงเหลือด้านบนจึงยังไม่ขยับตามยอดขาย
            </Alert>
          )}

          {colliding.length > 0 && balance?.eventEndsAt && (
            /* The warning worth reading is the collision, not the date. An organizer knows when
             * their event is; what they cannot see is that their credits die first. */
            <Alert tone="danger" title="เครดิตบางก้อนหมดอายุก่อนงานจบ">
              <p>
                งาน{balance.eventName ? ` ${balance.eventName}` : ''} จบวันที่ {day(balance.eventEndsAt)} แต่{' '}
                {credits(colliding.reduce((s, r) => s + r.lot.creditsRemaining, 0))} หมดอายุก่อนหน้านั้น
              </p>
              <ul className="jt-consequences">
                {colliding.map((r) => (
                  <li key={r.lot.id}>
                    {credits(r.lot.creditsRemaining)} · {expiryWords(r.lot)}
                    {r.lot.expiresAt ? ` (${day(r.lot.expiresAt)})` : ''}
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          <div className="jt-with-rail">
            <div className="jt-section">
              {topups.length > 0 && (
                <Card title="รายการเติมเครดิต" sub="เครดิตเข้าหลังจากแนบสลิปและมีคนตรวจแล้ว">
                  <ul className="jt-checks">
                    {topups.map((t) => (
                      <li key={t.id} className="jt-check">
                        <div className="jt-check-copy">
                          <p className="jt-check-label">
                            {fmt(t.amountDueThb)}
                            {t.carryoverDiscountThb > 0 && (
                              <span className="jt-check-blocking"> · หลังหักส่วนลด {fmt(t.carryoverDiscountThb)}</span>
                            )}
                          </p>
                          <p className="jt-check-detail">{dt(t.createdAt)}</p>
                          {t.status === 'pending' && (
                            <label className="jt-file-label">
                              แนบสลิปโอนเงิน
                              <input
                                type="file"
                                accept="image/*"
                                className="jt-sr-only"
                                disabled={busy}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) void uploadSlip(t.id, file);
                                  e.target.value = '';
                                }}
                              />
                            </label>
                          )}
                        </div>
                        <span className="jt-check-trail">
                          <StatusBadge tone={TOPUP_TONE[t.status]}>{TOPUP_LABEL[t.status]}</StatusBadge>
                        </span>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}

              <Card
                title="รายการเดินบัญชี"
                sub="ล่าสุดก่อน — ทุกครั้งที่เครดิตเข้าหรือออก"
                flush={statementLoaded && statement.length > 0}
              >
                {!statementLoaded ? (
                  <p className="jt-field-hint">กดปุ่ม “ดูรายการเดินบัญชี” ด้านบนเพื่อโหลด</p>
                ) : (
                  <DataTable
                    label="รายการเดินบัญชีเครดิต"
                    columns={ledgerColumns}
                    rows={statement}
                    rowKey={(r) => r.id}
                    emptyTitle="ยังไม่มีรายการ"
                    emptyText="เครดิตยังไม่เคยเข้าหรือออกจากบัญชีนี้"
                    card={(r) => (
                      <span className="jt-cell-stack">
                        <span>{KIND_LABEL[r.kind] ?? r.kind}</span>
                        <span className="jt-cell-sub">
                          {dt(r.created_at)}
                          {r.delta !== 0 && ` · ${r.delta > 0 ? '+' : ''}${r.delta.toLocaleString('th-TH')} ใบ`}
                        </span>
                      </span>
                    )}
                  />
                )}
              </Card>
            </div>

            <div className="jt-section">
              <Card title="เครดิตแต่ละก้อน" sub="ยอดคงเหลือด้านบนคือผลรวมของก้อนเหล่านี้">
                {!balance || balance.lots.length === 0 ? (
                  <p className="jt-field-hint">ยังไม่มีเครดิต</p>
                ) : (
                  <ul className="jt-checks">
                    {balance.lots.map((lot) => {
                      const risk = risks.find((r) => r.lot.id === lot.id);
                      return (
                        <li key={lot.id} className="jt-check">
                          <div className="jt-check-copy">
                            <p className="jt-check-label">
                              {lot.creditsRemaining.toLocaleString('th-TH')} / {lot.creditsTotal.toLocaleString('th-TH')} ใบ
                            </p>
                            <p className="jt-check-detail">
                              {SOURCE_LABEL[lot.source]}
                              {lot.unitPriceThb > 0 && ` · ${fmt(lot.unitPriceThb)}/ใบ`}
                              {` · ${expiryWords(lot)}`}
                            </p>
                            {risk?.diesBeforeEvent && (
                              <p className="jt-check-detail jt-tone-danger">หมดอายุก่อนงานจบ</p>
                            )}
                          </div>
                          <span className="jt-check-trail">
                            <StatusBadge tone={lot.live ? 'success' : 'neutral'}>
                              {lot.live ? 'ใช้ได้' : 'ปิดแล้ว'}
                            </StatusBadge>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Card>
            </div>
          </div>
        </>
      }
      detail={
        <>
          <Drawer
            open={buying}
            onClose={() => { setBuying(false); resetPurchase(); }}
            title="ซื้อเครดิต"
            dirty={Object.values(picks).some((q) => q > 0)}
            discardLabel="ทิ้งตะกร้า"
            footer={
              <>
                <Button variant="ghost" onClick={() => { setBuying(false); resetPurchase(); }}>ยกเลิก</Button>
                <Button
                  variant="primary"
                  disabled={!quote || busy}
                  onClick={() => setConfirmBuy(true)}
                >
                  สร้างรายการและรับเลขบัญชี
                </Button>
              </>
            }
          >
            <Card title="เลือกแพ็กเกจ" sub="ผสมได้ — ราคาคำนวณจากเซิร์ฟเวอร์ทุกครั้งที่เปลี่ยน">
              {tiers.map((tier) => (
                <div key={tier.id} className="jt-tier">
                  <div className="jt-tier-copy">
                    <p className="jt-check-label">{tier.credits.toLocaleString('th-TH')} เครดิต</p>
                    <p className="jt-check-detail">
                      {fmt(tier.unitPriceThb)}/ใบ · รวม {fmt(tier.grossThb)} · ใช้ได้ {tier.validDays} วัน
                    </p>
                  </div>
                  <div className="jt-stepper">
                    <Button
                      size="sm" variant="ghost"
                      aria-label={`ลดจำนวนแพ็กเกจ ${tier.credits}`}
                      onClick={() => setPick(tier.id, (picks[tier.id] ?? 0) - 1)}
                    >
                      −
                    </Button>
                    <span className="jt-mono jt-stepper-value">{picks[tier.id] ?? 0}</span>
                    <Button
                      size="sm" variant="ghost"
                      aria-label={`เพิ่มจำนวนแพ็กเกจ ${tier.credits}`}
                      onClick={() => setPick(tier.id, (picks[tier.id] ?? 0) + 1)}
                    >
                      +
                    </Button>
                  </div>
                </div>
              ))}
            </Card>

            {eligible.length > 0 && (
              <Card
                title="แลกเครดิตคงเหลือเป็นส่วนลด"
                sub="คืนให้ 50% ของมูลค่า สูงสุดไม่เกิน 30% ของยอดซื้อใหม่ · แลกได้ภายใน 60 วันหลังหมดอายุ"
              >
                {eligible.map((lot) => (
                  <label key={lot.id} className="jt-tradein">
                    <input
                      type="checkbox"
                      checked={tradeIn.includes(lot.id)}
                      onChange={() => toggleTradeIn(lot.id)}
                    />
                    <span>
                      <span className="jt-check-label">
                        {credits(lot.creditsRemaining)} · มูลค่า {fmt(lot.remainingValueThb)}
                      </span>
                      <span className="jt-check-detail">{expiryWords(lot)}</span>
                      {lot.live && (
                        <span className="jt-check-detail jt-tone-danger">
                          ยังใช้งานได้อยู่ — แลกแล้วเครดิตที่เหลือจะถูกปิดทันที
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </Card>
            )}

            {quoteError && <Alert tone="danger" title="คำนวณราคาไม่สำเร็จ">{quoteError}</Alert>}

            {quote && (
              <Card title="ยอดที่ต้องโอน">
                <ul className="jt-checks">
                  <li className="jt-check">
                    <div className="jt-check-copy"><p className="jt-check-label">ได้รับ</p></div>
                    <span className="jt-check-trail jt-mono">{credits(quote.totalCredits)}</span>
                  </li>
                  <li className="jt-check">
                    <div className="jt-check-copy"><p className="jt-check-label">ราคาเต็ม</p></div>
                    <span className="jt-check-trail jt-mono">{fmt(quote.grossThb)}</span>
                  </li>
                  {quote.carryoverDiscountThb > 0 && (
                    <li className="jt-check">
                      <div className="jt-check-copy">
                        <p className="jt-check-label">ส่วนลดจากเครดิตคงเหลือ</p>
                        {quote.carryoverDiscountThb < Math.floor(quote.carryoverValueThb * 0.5) && (
                          <p className="jt-check-detail">ติดเพดาน 30% ของยอดซื้อ</p>
                        )}
                      </div>
                      <span className="jt-check-trail jt-mono">−{fmt(quote.carryoverDiscountThb)}</span>
                    </li>
                  )}
                </ul>

                <p className="jt-quote-total">ยอดที่ต้องโอน {fmt(quote.amountDueThb)}</p>

                {cost.liveCreditsLost > 0 && (
                  <Alert tone="danger" title="การแลกนี้ทำให้เสียเครดิตที่ยังใช้ได้">
                    ยอมเสีย {credits(cost.liveCreditsLost)} ที่ยังใช้งานได้ เพื่อแลกส่วนลด{' '}
                    {fmt(quote.carryoverDiscountThb)}
                  </Alert>
                )}

                {quote.betterTier && (
                  <Alert tone="warning" title="มีแพ็กเกจที่ถูกกว่าและได้มากกว่า">
                    <p>
                      {quote.betterTier.credits.toLocaleString('th-TH')} เครดิต ราคา {fmt(quote.betterTier.grossThb)} ·
                      {' '}ใช้ได้ {quote.betterTier.validDays} วัน
                    </p>
                    <div className="jt-drawer-guard">
                      <Button size="sm" variant="secondary" onClick={() => chooseOnly(quote.betterTier!.id)}>
                        เปลี่ยนเป็นแพ็กเกจนี้
                      </Button>
                    </div>
                  </Alert>
                )}
              </Card>
            )}
          </Drawer>

          <ConfirmDialog
            open={confirmBuy}
            onClose={() => setConfirmBuy(false)}
            onConfirm={() => {
              setConfirmBuy(false);
              void buy().then((ok) => { if (ok) setBuying(false); });
            }}
            title="สร้างรายการเติมเครดิต"
            confirmLabel="สร้างรายการ"
            tone={cost.liveCreditsLost > 0 ? 'danger' : 'warning'}
            busy={busy}
            consequences={[
              `จะได้รับ ${quote ? credits(quote.totalCredits) : ''} และต้องโอน ${quote ? fmt(quote.amountDueThb) : ''}`,
              ...(cost.liveCreditsLost > 0
                ? [`เครดิตที่ยังใช้ได้ ${credits(cost.liveCreditsLost)} จะถูกปิดทันทีเพื่อแลกเป็นส่วนลด — ส่วนนี้เอาคืนไม่ได้`]
                : []),
              'เครดิตจะยังไม่เข้าจนกว่าจะแนบสลิปและมีคนตรวจสอบ',
            ]}
          />
        </>
      }
    />
  );
}

export function CreditsDestination({ token, eventSlug, destination, permitted, role }: {
  token: string;
  eventSlug?: string;
  destination: Destination;
  permitted: boolean;
  role?: string | null;
}) {
  const state = useCredits(token, eventSlug);
  return <CreditsScreen destination={destination} state={state} permitted={permitted} role={role} />;
}
